'use client'

import { createContext, useContext } from 'react'

import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import type { ComponentKey } from '@/features/dashboard/lib/widget-sizes'

// Both the chunk loader and Suspense read the same widget identity, without importing widgets.
export const WidgetLoadingContext = createContext<ComponentKey | null>(null)

function TableSkeleton({ columns, rows = 10 }: { columns: number; rows?: number }) {
  return (
    <div className="flex h-full flex-col gap-3">
      <div
        className="grid gap-3"
        style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
      >
        {Array.from({ length: columns }, (_, i) => (
          <Skeleton key={i} className="h-2 w-3/4" />
        ))}
      </div>
      {Array.from({ length: rows }, (_, row) => (
        <div
          key={row}
          className="grid gap-3"
          style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
        >
          {Array.from({ length: columns }, (_, col) => (
            <Skeleton
              key={col}
              className="h-3"
              style={{ width: `${55 + ((row + col) % 4) * 12}%` }}
            />
          ))}
        </div>
      ))}
    </div>
  )
}

function ChartSkeleton({ candles = true }: { candles?: boolean }) {
  return (
    <div className="flex h-full min-h-24 flex-col gap-3">
      <div className="flex gap-2">
        {[12, 20, 16].map((width, i) => (
          <Skeleton key={i} className="h-3" style={{ width: `${width}%` }} />
        ))}
      </div>
      <div className="flex min-h-0 flex-1 gap-3">
        <div className="flex min-w-0 flex-1 items-end justify-between gap-1">
          {Array.from({ length: 22 }, (_, i) => (
            <div
              key={i}
              className="relative flex h-full min-w-0 flex-1 items-center justify-center"
              style={{ transform: `translateY(${Math.sin(i / 3) * 18}%)` }}
            >
              <Skeleton className="w-px" style={{ height: `${35 + (i % 4) * 6}%` }} />
              <Skeleton
                className="absolute w-full"
                style={{ height: candles ? `${12 + (i % 3) * 5}%` : '3px' }}
              />
            </div>
          ))}
        </div>
        <div className="flex w-8 flex-col justify-between py-2">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-2 w-full" />
          ))}
        </div>
      </div>
      <div className="flex h-7 items-end gap-1">
        {Array.from({ length: 22 }, (_, i) => (
          <Skeleton key={i} className="flex-1" style={{ height: `${25 + ((i * 17) % 75)}%` }} />
        ))}
      </div>
      <div className="flex justify-between">
        {Array.from({ length: 5 }, (_, i) => (
          <Skeleton key={i} className="h-2 w-6" />
        ))}
      </div>
    </div>
  )
}

function BookSkeleton() {
  return (
    <div className="flex h-full flex-col gap-3">
      <div className="grid grid-cols-3 gap-3">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-2 w-3/4" />
        ))}
      </div>
      {[0, 1].map((side) => (
        <div key={side} className="flex flex-col gap-2">
          {side === 1 && (
            <div className="flex justify-between py-2">
              <Skeleton className="h-2 w-8" />
              <Skeleton className="h-5 w-20" />
              <Skeleton className="h-2 w-8" />
            </div>
          )}
          {Array.from({ length: 5 }, (_, i) => (
            <div key={i} className="grid grid-cols-3 items-center gap-3">
              <Skeleton className="h-3 w-4/5" />
              <Skeleton
                className="col-span-2 ml-auto h-4"
                style={{ width: `${25 + (side === 0 ? 4 - i : i) * 15}%` }}
              />
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}

function FormSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-2">
        <Skeleton className="h-7" />
        <Skeleton className="h-7" />
      </div>
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex flex-col gap-2">
          <Skeleton className="h-2 w-16" />
          <Skeleton className="h-8 w-full" />
        </div>
      ))}
      <div className="flex justify-between">
        <Skeleton className="h-2 w-16" />
        <Skeleton className="h-2 w-12" />
      </div>
      <Skeleton className="h-9 w-full" />
    </div>
  )
}

function CalendarSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-between">
        <Skeleton className="h-5 w-16" />
        <Skeleton className="h-5 w-24" />
        <Skeleton className="h-5 w-16" />
      </div>
      <div className="grid grid-cols-7 gap-2">
        {Array.from({ length: 7 }, (_, i) => (
          <Skeleton key={i} className="h-2 w-3/4" />
        ))}
        {Array.from({ length: 35 }, (_, i) => (
          <div key={i} className="flex h-12 flex-col gap-2">
            <Skeleton className="h-2 w-3" />
            {i % 3 !== 0 && <Skeleton className="h-2 w-full" />}
            {i % 5 === 0 && <Skeleton className="h-2 w-3/4" />}
          </div>
        ))}
      </div>
    </div>
  )
}

function SummarySkeleton() {
  return (
    <div className="flex h-full flex-col gap-5">
      <Skeleton className="h-2 w-20" />
      <Skeleton className="h-7 w-32" />
      <div className="grid grid-cols-3 gap-4">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex flex-col gap-2">
            <Skeleton className="h-2 w-3/4" />
            <Skeleton className="h-4 w-full" />
          </div>
        ))}
      </div>
      <ChartSkeleton candles={false} />
    </div>
  )
}

function FeedSkeleton() {
  return (
    <div className="flex flex-col gap-5">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="flex flex-col gap-2">
          <div className="flex justify-between">
            <Skeleton className="h-2 w-16" />
            <Skeleton className="h-2 w-10" />
          </div>
          <Skeleton className="h-3 w-11/12" />
          <Skeleton className="h-3 w-3/4" />
          <Skeleton className="h-2 w-1/2" />
        </div>
      ))}
    </div>
  )
}

function WidgetBody({ kind }: { kind: ComponentKey | null }) {
  switch (kind) {
    case 'price-chart':
      return <ChartSkeleton />
    case 'order-book':
    case 'quick-order':
      return <BookSkeleton />
    case 'watchlist':
      return <TableSkeleton columns={4} />
    case 'trades':
      return <TableSkeleton columns={3} />
    case 'positions':
      return <TableSkeleton columns={7} />
    case 'economic-calendar':
      return <CalendarSkeleton />
    case 'account-cards':
      return <SummarySkeleton />
    case 'newsfeed':
      return <FeedSkeleton />
    case 'chat-order-form':
      return <FormSkeleton />
    case 'ticker':
      return <ChartSkeleton candles={false} />
    case 'data':
      return <ChartSkeleton candles={false} />
    case 'risk-slides':
      return (
        <>
          <div className="grid grid-cols-3 gap-2 pb-4">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-7" />
            ))}
          </div>
          <TableSkeleton columns={5} rows={7} />
        </>
      )
    case 'spreader':
      return <TableSkeleton columns={5} rows={8} />
    case 'one-tap-buy':
      return (
        <div className="grid grid-cols-2 gap-3">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex flex-col gap-2">
              <Skeleton className="h-3 w-2/3" />
              <Skeleton className="h-16 w-full" />
            </div>
          ))}
        </div>
      )
    case 'preset-manager':
      return (
        <div className="flex flex-col gap-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className="size-8" />
              <div className="flex flex-1 flex-col gap-2">
                <Skeleton className="h-3 w-3/4" />
                <Skeleton className="h-2 w-1/2" />
              </div>
              <Skeleton className="h-5 w-8" />
            </div>
          ))}
        </div>
      )
    default:
      return <TableSkeleton columns={3} rows={6} />
  }
}

export function WidgetLoadingFallback() {
  const kind = useContext(WidgetLoadingContext)
  return (
    <Card aria-hidden="true" className="flex h-full w-full min-w-0 flex-col">
      <CardHeader className="h-9 shrink-0 flex-row items-center justify-between space-y-0 px-3">
        <div className="flex min-w-0 items-center gap-3">
          <Skeleton className="size-3" />
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-3 w-12" />
        </div>
        <Skeleton className="h-3 w-2" />
      </CardHeader>
      <CardContent className="flex min-h-0 flex-1 flex-col overflow-hidden p-3">
        <WidgetBody kind={kind} />
      </CardContent>
    </Card>
  )
}

export const WIDGET_LOADING_FALLBACK = <WidgetLoadingFallback />
